import { writable, derived } from 'svelte/store';
import { logger } from '../utils/logger.js';
import { env } from '../config/env.js';

/**
 * @typedef {Object} InventoryItem
 * @property {string} id - SKU (Unique identifier)
 * @property {string} name - Peça/Categoria (Product name/category)
 * @property {string} description - Descrição site (Product description for website)
 * @property {number} price - Preço venda (Sale price in BRL)
 * @property {string} category - Peça/Categoria (Category)
 * @property {string} brand - Marca (Brand)
 * @property {string} color - Cor (Color)
 * @property {string} size - Tamanho que veste (Size it fits)
 * @property {string} sizeLabel - Tamanho etiqueta (Label size)
 * @property {string} material - Material
 * @property {string} condition - Estado (Condition)
 * @property {string} measurements - Medidas (B/C/Q/Comp)
 * @property {string} details - Detalhes (Additional details)
 * @property {string} occasion - Ocasião (Occasion)
 * @property {string} season - Estação (Season)
 * @property {string} imageUrl - Main image URL from Link da pasta
 * @property {string[]} images - Additional image URLs from folder
 * @property {boolean} available - Status (disponível/vendido)
 * @property {string} status - Status original
 * @property {string} createdAt - Data entrada (Entry date)
 * @property {string} soldAt - Data venda (Sale date)
 * @property {string} folderLink - Link da pasta (Google Drive folder link)
 * @property {string} instagramCaption - Legenda Instagram
 * @property {string} tiktokCaption - Legenda TikTok
 */

/**
 * Inventory state
 */
const inventoryState = writable({
  items: /** @type {InventoryItem[]} */ ([]),
  isLoading: false,
  error: /** @type {string | null} */ (null),
  lastFetch: /** @type {number | null} */ (null)
});

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

/**
 * Parse Google Sheets CSV data
 * @param {string} csvData - Raw CSV data
 * @returns {InventoryItem[]}
 */
function parseCSV(csvData) {
  const lines = csvData.split('\n');
  if (lines.length < 2) return [];

  // First line is headers
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  
  // Map header names to our field names
  const headerMap = {
    'sku': 'sku',
    'status': 'status',
    'peça/categoria': 'category',
    'marca': 'brand',
    'cor': 'color',
    'tamanho etiqueta': 'sizeLabel',
    'tamanho que veste': 'size',
    'material': 'material',
    'estado': 'condition',
    'medidas': 'measurements',
    'tem etiqueta': 'hasLabel',
    'ocasião': 'occasion',
    'estação': 'season',
    'forro': 'lining',
    'bolsos': 'pockets',
    'elasticidade': 'elasticity',
    'detalhes': 'details',
    'observações adicionais': 'additionalNotes',
    'obs. interna': 'internalNotes',
    'fornecedor': 'supplier',
    'preço custo': 'costPrice',
    'markup (%)': 'markup',
    'preço venda': 'price',
    'lucro líquido': 'netProfit',
    'data entrada': 'entryDate',
    'data venda': 'saleDate',
    'dias em estoque': 'daysInStock',
    'canal venda': 'salesChannel',
    'link da pasta': 'folderLink',
    'canal publicado': 'publishedChannel',
    'legenda instagram': 'instagramCaption',
    'legenda tiktok': 'tiktokCaption',
    'descrição site': 'description',
    'aviso whatsapp': 'whatsappNotice'
  };
  
  const items = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    if (values.length < headers.length) continue;
    
    const row = {};
    headers.forEach((header, index) => {
      const mappedKey = headerMap[header] || header;
      row[mappedKey] = values[index]?.trim() || '';
    });

    // Skip if no SKU
    if (!row.sku) continue;

    // Determine if item is available
    const isAvailable = row.status && 
      row.status.toLowerCase() !== 'vendido' && 
      row.status.toLowerCase() !== 'sold';

    // Parse price
    const price = parseFloat(
      row.price?.replace(/[^\d,.-]/g, '').replace(',', '.') || '0'
    );

    // Extract images from folder link
    let imageUrl = '';
    let images = [];
    if (row.folderLink) {
      // If it's a Google Drive folder, we'll need to fetch the images
      // For now, we'll use a placeholder approach
      imageUrl = extractFirstImageFromFolder(row.folderLink);
      images = extractImagesFromFolder(row.folderLink);
    }

    // Map to our item structure
    items.push({
      id: row.sku,
      name: row.category || 'Sem nome',
      description: row.description || row.details || '',
      price: price,
      category: normalizeCategory(row.category || ''),
      brand: row.brand || '',
      color: row.color || '',
      size: row.size || row.sizeLabel || 'Único',
      sizeLabel: row.sizeLabel || '',
      material: row.material || '',
      condition: row.condition || 'Bom estado',
      measurements: row.measurements || '',
      details: row.details || '',
      occasion: row.occasion || '',
      season: row.season || '',
      imageUrl: imageUrl,
      images: images,
      available: isAvailable,
      status: row.status || '',
      createdAt: row.entryDate || '',
      soldAt: row.saleDate || '',
      folderLink: row.folderLink || '',
      instagramCaption: row.instagramCaption || '',
      tiktokCaption: row.tiktokCaption || ''
    });
  }

  return items.filter(item => item.id);
}

/**
 * Parse a single CSV line handling quoted values
 * @param {string} line - CSV line
 * @returns {string[]}
 */
function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  
  result.push(current);
  return result;
}

/**
 * Convert Google Drive sharing URL to direct image URL
 * @param {string} url - Original URL
 * @returns {string}
 */
function convertDriveUrl(url) {
  if (!url) return '';
  
  // Handle Google Drive file URLs
  const driveMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
  if (driveMatch) {
    return `https://drive.google.com/uc?export=view&id=${driveMatch[1]}`;
  }
  
  // Handle already converted URLs or other image URLs
  return url;
}

/**
 * Normalize category name to match our CATEGORIES constants
 * @param {string} category - Original category
 * @returns {string}
 */
function normalizeCategory(category) {
  const normalized = category.toLowerCase();
  
  if (normalized.includes('roupa') || normalized.includes('vestuário') || 
      normalized.includes('blusa') || normalized.includes('vestido') || 
      normalized.includes('calça') || normalized.includes('saia')) {
    return 'roupas';
  }
  if (normalized.includes('acessório') || normalized.includes('acessorio') || 
      normalized.includes('joia') || normalized.includes('bijuteria')) {
    return 'acessorios';
  }
  if (normalized.includes('calçado') || normalized.includes('sapato') || 
      normalized.includes('tênis') || normalized.includes('sandália')) {
    return 'calcados';
  }
  if (normalized.includes('bolsa') || normalized.includes('mochila') || 
      normalized.includes('carteira')) {
    return 'bolsas';
  }
  
  return 'outros';
}

/**
 * Extract first image from Google Drive folder link
 * @param {string} folderLink - Google Drive folder link
 * @returns {string}
 */
function extractFirstImageFromFolder(folderLink) {
  if (!folderLink) return '';
  
  // For now, return empty - this would require Google Drive API
  // which needs authentication. We'll handle this separately.
  return '';
}

/**
 * Extract all images from Google Drive folder link
 * @param {string} folderLink - Google Drive folder link
 * @returns {string[]}
 */
function extractImagesFromFolder(folderLink) {
  if (!folderLink) return [];
  
  // For now, return empty - this would require Google Drive API
  // which needs authentication. We'll handle this separately.
  return [];
}

/**
 * Fetch inventory from backend API (Netlify Function)
 * Falls back to direct Google Sheets CSV if backend unavailable
 */
export async function fetchInventory() {
  const state = getInventoryState();
  
  // Check cache
  if (state.lastFetch && Date.now() - state.lastFetch < CACHE_DURATION && state.items.length > 0) {
    logger.debug('Using cached inventory');
    return;
  }

  inventoryState.update(s => ({ ...s, isLoading: true, error: null }));

  try {
    let items;

    // Fetch from backend API (handles private Google Sheets via service account)
    const apiUrl = '/.netlify/functions/inventory';
    logger.info('Fetching inventory from backend API:', apiUrl);
    
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      const errorText = await response.text();
      logger.error('Backend API error:', response.status, errorText);
      throw new Error(`Backend API error: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    logger.info('Backend response:', { 
      itemCount: data.items?.length,
      total: data.total,
      timestamp: data.timestamp 
    });
    
    // Log each item received
    data.items?.forEach((item, index) => {
      console.log(`[Backend Item ${index}]`, {
        id: item.id,
        name: item.name,
        available: item.available,
        category: item.category,
        price: item.price
      });
    });
    
    // Backend returns pre-formatted items
    if (!data.items || data.items.length === 0) {
      logger.warn('No items returned from backend');
      items = [];
    } else {
      items = data.items;
      logger.info(`Loaded ${items.length} items from backend API`);
    }

    inventoryState.set({
      items,
      isLoading: false,
      error: null,
      lastFetch: Date.now()
    });
    
    console.log('[inventoryService] State updated with items:', items.length);
    console.log('[inventoryService] Items in state:', items.map(i => ({ id: i.id, available: i.available })));

    logger.info(`Loaded ${items.length} items from inventory`);
  } catch (error) {
    logger.error('Failed to fetch inventory:', error);
    
    inventoryState.update(s => ({
      ...s,
      isLoading: false,
      error: error.message || 'Erro ao carregar produtos'
    }));
  }
}

/**
 * Get current inventory state (for non-reactive access)
 */
function getInventoryState() {
  let state;
  inventoryState.subscribe(s => state = s)();
  return state;
}

/**
 * Inventory store (read-only)
 */
export const inventory = {
  subscribe: inventoryState.subscribe
};

/**
 * Derived store for available items only
 */
export const availableItems = derived(inventoryState, ($state) => 
  $state.items.filter(item => item.available)
);

/**
 * Derived store for loading state
 */
export const isLoading = derived(inventoryState, ($state) => $state.isLoading);

/**
 * Derived store for error state
 */
export const inventoryError = derived(inventoryState, ($state) => $state.error);

/**
 * Get items by category
 * @param {string} category - Category name
 */
export function getItemsByCategory(category) {
  return derived(inventoryState, ($state) => 
    $state.items.filter(item => 
      item.available && 
      item.category.toLowerCase() === category.toLowerCase()
    )
  );
}

/**
 * Search items by name or description
 * @param {string} query - Search query
 */
export function searchItems(query) {
  const lowerQuery = query.toLowerCase();
  return derived(inventoryState, ($state) => 
    $state.items.filter(item => 
      item.available && (
        item.name.toLowerCase().includes(lowerQuery) ||
        item.description.toLowerCase().includes(lowerQuery)
      )
    )
  );
}

/**
 * Get a single item by ID
 * @param {string} id - Item ID
 */
export function getItemById(id) {
  return derived(inventoryState, ($state) => 
    $state.items.find(item => item.id === id)
  );
}

/**
 * Force refresh inventory (bypass cache)
 */
export async function refreshInventory() {
  inventoryState.update(s => ({ ...s, lastFetch: null }));
  await fetchInventory();
}
